import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Autocomplete, Box, CircularProgress, TextField, Typography, Dialog, DialogContent, Button, Stack, Tooltip, IconButton } from '@mui/material';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../utils/axios';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import UOMSelectMenu from './UOMSelectMenu';
import RequestQuoteButton from './RequestQuoteButton';
import AGGridPictureCol from '../../reuseable-components/AGGrid/AGGridPictureCol';
import { objectToFormData } from '../../utils/objectToFormData';

export default function OrderSheetTable() {
  const { state } = useLocation();
  const id = state?.facilityId;
  const gridRef = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate()

  const [products, setProducts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [carts, setCarts] = useState([]);
  const [selectedCart, setSelectedCart] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingItemsToCart, setAddingItemsToCart] = useState(false);
  const [fromData, setFromData] = useState([]);
  const [customerPurchaseOrderNumber, setCustomerPurchaseOrderNumber] = useState(null);
  const [tempPON, setTempPON] = useState("");
  const [openPurchaseOrderNumber, setOpenPurchaseOrderNumber] = useState(false);
  function compareProducts(a, b) {
    const ap = a.activeUOM?.price ?? -1;
    const bp = b.activeUOM?.price ?? -1;
    if (ap === -1 && bp === -1) return 0;
    if (ap !== -1 && bp === -1) return -1;
    if (ap === -1 && bp !== -1) return 1;
    return ap - bp;
  }

  const showVendorCols = useMemo(() => products?.some(product => product.vendor) ?? false, [products])

  const mapProducts = (prods) => setProducts((prods ?? []).map(prod => ({ ...prod, activeUOM: prod.uomTypes.find(uom => uom.price !== null) ?? prod.uomTypes[0] }))
    .sort(compareProducts));

  useEffect(() => {
    const facilityIdFromQuery = Number(searchParams.get("facilityId"));
    const facilityId = id ?? facilityIdFromQuery;
    const productCategoryId = Number(searchParams.get("productCategoryId"));
    axiosInstance.get(`/orderSheet/orderSheetData?facilityId=${facilityId}&productCategoryId=${productCategoryId}`)
      .then(res => {
        mapProducts(res.data.products);
        setFromData([]);
        setFacilities(res.data.facilities);
        setCategories([{ value: -1, label: "All Categories" }, ...res.data.categories]);
        setSelectedCategory(res.data.categories.find(category => category.value === res.data.categoryId));
        setSelectedFacility(res.data.facilities.find(facility => facility.value === res.data.facilityId));
      })
      .catch(() => enqueueSnackbar('Product list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedFacility?.value) {
      setSelectedCart(null);
      fetchCarts();
    }
  }, [selectedFacility]);

  const fetchData = (facilityId, categoryId) => {
    if (facilityId && categoryId) {
      setLoading(true);
      axiosInstance.get(`/orderSheet/orderSheetProducts?facilityId=${facilityId}&productCategoryId=${categoryId}`)
        .then(res => mapProducts(res.data))
        .catch(() => enqueueSnackbar('Product list failed', { variant: 'error' }))
        .finally(() => setLoading(false));
    } else setProducts([]);
  }

  const fetchCarts = () => {
    if (selectedFacility.value) {
      axiosInstance.get(`/cart/carts?facilityId=${selectedFacility.value}`)
        .then(res => {
          setCarts(["New Cart", ...res.data.purchaseOrderNumber])
        })
        .catch(() => enqueueSnackbar('Carts list failed', { variant: 'error' }));
    }
  }

  const addItemsToCart = () => {
    if (!selectedCategory?.value || !selectedFacility?.value) return;
    setAddingItemsToCart(true);
    if (selectedCart === "New Cart") {
      setOpenPurchaseOrderNumber(true);
    } else {
      addToCartByPONumber(selectedCart);
    }
  };

  const addToCartByPONumber = (purchaseOrderNumber, flag) => {
    axiosInstance.post(`/orderSheet/addToCart`, objectToFormData({ facilityId: selectedFacility?.value, purchaseOrderNumber, cartInputs: fromData }))
      .then(() => {
        setProducts(products => products.map(prod => ({ ...prod, quantity: 0 })));
        setFromData([]);
        enqueueSnackbar(flag ? 'Cart created' : 'Cart updated', { variant: 'success' });
      })
      .catch(() => enqueueSnackbar('Add to cart failed', { variant: 'error' }))
      .finally(() => setAddingItemsToCart(false));
  };

  const createNewCart = () => {
    setOpenPurchaseOrderNumber(false);
    axiosInstance.post(`/cart/createCart?facilityId=${selectedFacility.value}&purchaseOrderNumber=${encodeURIComponent(tempPON)}`)
      .then(res => {
        setSelectedCart(res.data.purchaseOrderNumber);
        setCarts([...carts, res.data.purchaseOrderNumber])
        const flag = true;
        addToCartByPONumber(res.data.purchaseOrderNumber, flag);
      })
      .catch(() => {
        enqueueSnackbar('Create cart failed', { variant: 'error' });

      })
      .finally(() => { setAddingItemsToCart(false); setCustomerPurchaseOrderNumber(tempPON); setTempPON("") })
  };

  const changeQuantity = (params, event) => {
    params.node.data.quantity = Number(event.target.value?.replace(/[^0-9]/g, '') ?? 0);
    params.node.setData(params.node.data);
    const results = [];
    params.api.forEachNode(node => {
      if (node.data.quantity) results.push({ quantity: node.data.quantity, price: node.data.activeUOM?.price ?? 0, productUOMId: node.data.activeUOM?.id ?? 0 })
    });
    setProducts(products => {
      const product = products.find(product => product.id === params.node.data.id);
      product.quantity = params.node.data.quantity;
      return products;
    });
    setFromData(results);
  };

  const changeUOM = (params, uom) => {
    params.node.data.activeUOM = uom;
    params.node.setData(params.node.data);
    const results = [];
    params.api.forEachNode(node => {
      if (node.data.quantity) results.push({ quantity: node.data.quantity, price: node.data.activeUOM?.price ?? 0, productUOMId: node.data.activeUOM?.id ?? 0 })
    });
    setProducts(products => {
      const product = products.find(product => product.id === params.node.data.id);
      product.activeUOM = uom;
      return products;
    });
    setFromData(results);
  };

  const checkCartExist = (value) => {
    if (carts.includes(value)) {
      return true;
    }
    return false;
  }

  const columns = useMemo(() => [
    AGGridPictureCol(),
    {
      field: 'name',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'id',
      headerName: 'SKU',
    },
    ...(showVendorCols ? [
      {
        field: 'vendor.vendor',
        headerName: 'Vendor',
      },
      {
        field: 'vendor.vendorItemId',
        headerName: 'Vendor Item Id',
      }
    ] : []),
    {
      field: 'uomTypes',
      headerName: 'UOM Type',
      cellRenderer: (params) => <UOMSelectMenu changeUOM={changeUOM} params={params} />
    },
    {
      field: 'uomPrice',
      headerName: 'Price',
      cellRenderer: (params) => params.data.activeUOM?.price !== null && params.data.activeUOM?.price !== undefined ?
        params.data.activeUOM.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        : "No Price",
      valueGetter: (params) => params.data.activeUOM?.price ?? 0
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      minWidth: 150,
      cellRenderer: (params) => params.data.activeUOM?.price !== null && params.data.activeUOM?.price !== undefined ?
        <TextField sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, marginTop: "3px", width: 100 }} size="small" value={params.data.quantity ?? 0}
          onChange={(e) => changeQuantity(params, e)} />
        : <RequestQuoteButton params={params} setProducts={setProducts} facilityId={selectedFacility?.value} />
    },
  ], [selectedFacility, showVendorCols]);


  const onlyOneFacility = !facilities || facilities.length < 2;

  return (<>
    <Helmet>
      <title>Order Sheet | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      {onlyOneFacility && <Typography component="h3" variant="">Facility: {selectedFacility?.label}</Typography>}
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        {id && (
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: 'black', "&:hover": { backgroundColor: "transparent", }, }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        {!onlyOneFacility && <Box sx={{ width: { xs: '100%', md: 300 } }}>
          <Typography variant="subtitle1">Facility</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={selectedFacility ?? null}
            onChange={(event, val) => { setSelectedFacility(val); fetchData(val?.value, selectedCategory?.value); }}
            options={facilities}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder="Select Facility" />}
          />
        </Box>}
        <Box sx={{ width: { xs: '100%', md: 300 } }}>
          <Typography variant="subtitle1">Category</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={selectedCategory ?? null}
            onChange={(event, val) => { setSelectedCategory(val); fetchData(selectedFacility?.value, val?.value); }}
            options={categories}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder="Select Category" />}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', md: 150 } }}>
          <Typography variant="subtitle1">Cart</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={selectedCart ?? null}
            onChange={(event, val) => { setSelectedCart(val); }}
            options={carts}
            getOptionLabel={(option) => option || ''}
            renderOption={(props, option, { index }) => (
              <li {...props} key={`${option}-${index}`}>
                {option}
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder="Select Cart" />}
          />
        </Box>
        <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <Tooltip title={!fromData?.some(product => product) && selectedCart === null ? "Select products and cart" : !fromData?.some(product => product) ? "Select products" : selectedCart === null ? "Select cart" : ""}
            arrow placement="top">
            <span>
              <LoadingButton loading={addingItemsToCart} variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} disabled={!fromData?.some(product => product) || selectedCart === null}
                sx={{ marginTop: "auto" }} onClick={addItemsToCart} color="primary" size="small">
                <Typography>Add Items To Cart{fromData.length ? ` (${fromData.reduce((acc, prod) => acc + prod.quantity, 0)} - ${fromData.reduce((acc, prod) => acc + prod.quantity * prod.price, 0)
                  .toLocaleString('en-US', { style: 'currency', currency: 'USD' })})` : ""}</Typography>
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={products} loading={loading} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </Box>
    <Dialog open={openPurchaseOrderNumber} onClose={() => { setOpenPurchaseOrderNumber(false); setAddingItemsToCart(false); setTempPON("") }} maxWidth="sm" fullWidth>
      <DialogContent>
        <Stack spacing={1}>
          <Box>
            <Box>
              <Typography variant="subtitle1">PO Number</Typography>
              <TextField name="customerPurchaseOrderNumber" size="small" placeholder="Enter Customer Purchase Number" fullWidth value={tempPON}
                onChange={(e) => setTempPON(e.target.value)} />
            </Box>
            <Box>
              <Button sx={{ display: "flex", justifySelf: "center", mb: 2, mt: 2, px: 7 }} color="purple" size="medium"
                variant="contained" disabled={!tempPON}
                onClick={() => {
                  if (checkCartExist(tempPON)) {
                    enqueueSnackbar('Cart with this PO Number already exists', { variant: 'error' });
                    setTempPON("")
                  } else createNewCart(); }}>
                Confirm
              </Button>

            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>;
  </>
  );
}
